<script>
	// Stores
	import SportEventStore from '@/Stores/Sport/SportEventStore.js';
	// Services
	import { notify } from '@/Services/Notify.js';
	// Constants
	import { EventStatus } from '@/Constants/Event.js';
	// Components
	import Modal from '@/Components/ModalFlex.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';

	export let event;
	export let readOnly = false;

	const StatusColours = {
		[EventStatus.Open]: 'green-500',
		[EventStatus.InPlay]: 'blue-500',
		[EventStatus.Closed]: 'red-600',
		[EventStatus.Paid]: 'gray-500',
		[EventStatus.Abandoned]: 'orange-400',
		[EventStatus.MatchOver]: 'grey'
	};

	let open = false;
	let sending = false;

	$: statusLabel = event && getStatusLabel();

	function getStatusLabel() {
		return !event.feed_status
			? `Status: ${event.status.name}`
			: event.override_status
				? `Override status: ${event.override_status.name}, Feed status: ${event.feed_status.name}`
				: `Feed status: ${event.feed_status.name}`;
	}

	function onUpdateStatus(statusId = null) {
		sending = true;
		SportEventStore.updateOverrideStatus(event.id, statusId)
			.then(() => {
				notify({
					type: 'success',
					title: event.name,
					message: 'Event Status Updated',
					timeout: 3
				});
				open = false;
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update Event',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
			});
	}
</script>

{#if open}
	<Modal on:close={() => (open = false)}>
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">Override Event Status</h2>
		<div class="pt-1 italic">{event.name}</div>
		<div class="flex justify-center py-2 space-x-4">
			{#if event.feed_status}
				<p>Feed Status: {event.feed_status.name}</p>
			{:else}
				<p>Current Status: {event.status.name}</p>
			{/if}
			{#if event.override_status}
				<p>Override Status: {event.override_status.name}</p>
			{/if}
		</div>
		<div class="flex flex-col w-full px-4 pt-2 text-center">
			<div class="flex justify-center space-x-4">
				<LoadingButton
					on:click={() => onUpdateStatus(EventStatus.Open)}
					loading={sending}
					className="mb-4 btn btn-primary-action {`bg-${StatusColours[EventStatus.Open]}`}"
				>
					Open
				</LoadingButton>
				<LoadingButton
					on:click={() => onUpdateStatus(EventStatus.InPlay)}
					loading={sending}
					className="mb-4 btn btn-primary-action {`bg-${StatusColours[EventStatus.InPlay]}`}"
				>
					In Play
				</LoadingButton>
				<LoadingButton
					on:click={() => onUpdateStatus(EventStatus.Closed)}
					loading={sending}
					className="mb-4 btn btn-primary-action {`bg-${StatusColours[EventStatus.Closed]}`}"
				>
					Closed
				</LoadingButton>
				<LoadingButton
					on:click={() => onUpdateStatus(EventStatus.MatchOver)}
					loading={sending}
					className="mb-4 btn btn-primary-action {`bg-${StatusColours[EventStatus.MatchOver]}`}"
				>
					Match Over
				</LoadingButton>
				<LoadingButton
					on:click={() => onUpdateStatus()}
					loading={sending}
					className="mb-4 btn btn-primary-action bg-gray-500"
				>
					Reset
				</LoadingButton>
			</div>
			<button class="mb-4 btn btn-muted" on:click|preventDefault={() => (open = false)}> Cancel</button>
		</div>
	</Modal>
{/if}
<div class="flex justify-center space-x-1">
	<div
		title={statusLabel}
		class="w-4 h-4 {`bg-${
			StatusColours[event.feed_status && event.feed_status.id ? event.feed_status.id : event.status.id]
		}`} rounded-full
    cursor-help"
	>
		{#if event.override_status && event.override_status.id}
			<div
				class="h-2 {`bg-${StatusColours[event.override_status.id]}`} border-b
        border-white rounded-t-full"
			/>
		{/if}
	</div>
	{#if readOnly}
		<span class="ml-2">
			{event.display_status}
		</span>
	{:else}
		<button
			title="Change Event Status"
			class="focus:outline-none hover:text-green-500"
			on:click|preventDefault|stopPropagation={() => (open = true)}
		>
			{event.override_status ? event.override_status.name : event.status.name}
		</button>
	{/if}
</div>
